import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { dateTime } from '../../lib/format';

/**
 * The register of requests from authorities for personal data SARTHI holds.
 *
 * A record and a decision trail, not a way to hand data over: nothing here
 * reads or exports a hotel's records. Whoever logs a request cannot decide
 * it, and an approval needs the legal reference it relies on.
 */

type Status = 'RECEIVED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED' | 'PARTIALLY_APPROVED' | 'DISCLOSED' | 'CLOSED';

interface Disclosure {
  id: string;
  requestReference: string;
  authority: string;
  requestType: string;
  receivedAt: string;
  requestedData: string;
  tenantId: string | null;
  propertyId: string | null;
  legalReference: string | null;
  status: Status;
  receivedById: string;
  reviewNotes: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  decisionNotes: string | null;
  disclosedAt: string | null;
  disclosedTo: string | null;
  disclosureSummary: string | null;
  closedAt: string | null;
}

const STATUS_LABELS: Record<Status, string> = {
  RECEIVED: 'Received',
  UNDER_REVIEW: 'Under review',
  APPROVED: 'Approved',
  PARTIALLY_APPROVED: 'Partly approved',
  REJECTED: 'Rejected',
  DISCLOSED: 'Disclosed',
  CLOSED: 'Closed',
};

export function DisclosuresTab() {
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<Disclosure | null>(null);
  const list = useQuery({
    queryKey: ['platform-disclosures'],
    queryFn: () => api.get<Disclosure[]>('/api/platform/privacy/disclosures'),
  });
  if (list.isLoading) return <Loading />;
  if (list.error) return <ErrorState error={list.error} onRetry={() => void list.refetch()} />;
  const rows = list.data ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Disclosure requests</h3>
        <button className="btn sm primary" onClick={() => setCreating(true)}>
          Log a request
        </button>
      </div>
      <div className="card-body small muted">
        Requests from government, police, courts and other authorities. Logging one releases nothing. A second
        person decides it, against the legal reference it relies on; what was handed over, and to whom, is
        recorded afterwards. Follow docs/privacy/GOVERNMENT-DISCLOSURE-PROCEDURE.md.
      </div>
      {rows.length === 0 ? (
        <Empty message="No requests logged" />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Reference</th>
                <th>Authority</th>
                <th>Received</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="clickable" onClick={() => setOpen(row)}>
                  <td className="mono">{row.requestReference}</td>
                  <td>
                    <b>{row.authority}</b>
                    <div className="xs muted">{row.requestType}</div>
                  </td>
                  <td>{dateTime(row.receivedAt)}</td>
                  <td>
                    <span className="chip">{STATUS_LABELS[row.status]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {creating ? <CreateDisclosure onClose={() => setCreating(false)} /> : null}
      {open ? <DisclosureDetail id={open.id} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function CreateDisclosure({ onClose }: { onClose: () => void }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [authority, setAuthority] = useState('');
  const [requestType, setRequestType] = useState('');
  const [receivedAt, setReceivedAt] = useState(() => new Date().toISOString().slice(0, 16));
  const [requestedData, setRequestedData] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [legalReference, setLegalReference] = useState('');
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/platform/privacy/disclosures', {
        authority,
        requestType,
        receivedAt: new Date(receivedAt).toISOString(),
        requestedData,
        ...(tenantId ? { tenantId } : {}),
        legalReference,
      }),
    onSuccess: () => {
      push('Request logged', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-disclosures'] });
      onClose();
    },
    onError: fail,
  });
  return (
    <Modal
      title="Log a disclosure request"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={create.isPending || !authority.trim() || !requestType.trim() || requestedData.trim().length < 3}
            onClick={() => create.mutate()}
          >
            Log it
          </button>
        </>
      }
    >
      <div className="col gap-14">
        <TextInput label="Authority" value={authority} onChange={(e) => setAuthority(e.target.value)} />
        <TextInput label="Kind of request" hint="Notice, order, summons, letter…" value={requestType} onChange={(e) => setRequestType(e.target.value)} />
        <TextInput label="Received" type="datetime-local" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
        <Field label="What is asked for">
          <textarea className="input" rows={4} value={requestedData} onChange={(e) => setRequestedData(e.target.value)} />
        </Field>
        <TextInput label="Account id (if about one hotel)" value={tenantId} onChange={(e) => setTenantId(e.target.value.trim())} />
        <TextInput label="Legal reference (if stated)" value={legalReference} onChange={(e) => setLegalReference(e.target.value)} />
      </div>
    </Modal>
  );
}

function DisclosureDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { session } = useAuth();
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const detail = useQuery({
    queryKey: ['platform-disclosure', id],
    queryFn: () => api.get<Disclosure>(`/api/platform/privacy/disclosures/${id}`),
  });
  const [notes, setNotes] = useState('');
  const [legalReference, setLegalReference] = useState('');
  const [decision, setDecision] = useState('APPROVED');
  const [disclosedTo, setDisclosedTo] = useState('');
  const [summary, setSummary] = useState('');

  const act = useMutation({
    mutationFn: ({ path, payload }: { path: string; payload?: unknown }) =>
      api.post(`/api/platform/privacy/disclosures/${id}/${path}`, payload ?? {}),
    onSuccess: () => {
      push('Saved', 'ok');
      setNotes('');
      void queryClient.invalidateQueries({ queryKey: ['platform-disclosures'] });
      void queryClient.invalidateQueries({ queryKey: ['platform-disclosure', id] });
    },
    onError: fail,
  });

  if (detail.isLoading || !detail.data) {
    return (
      <Modal title="Disclosure request" onClose={onClose}>
        {detail.error ? <ErrorState error={detail.error} /> : <Loading />}
      </Modal>
    );
  }
  const row = detail.data;
  const loggedByMe = row.receivedById === session?.user.id;

  return (
    <Modal title={`${row.requestReference} - ${STATUS_LABELS[row.status]}`} onClose={onClose} size="wide">
      <div className="col gap-14">
        <div className="small">
          <b>{row.authority}</b> · {row.requestType} · received {dateTime(row.receivedAt)}
        </div>
        <div className="privacy-doc small">{row.requestedData}</div>
        <div className="small">
          Legal reference: {row.legalReference ?? <span className="chip warn">Not recorded</span>}
        </div>
        {row.reviewNotes ? <div className="small">Review: {row.reviewNotes}</div> : null}
        {row.decisionNotes ? <div className="small">Decision: {row.decisionNotes}</div> : null}
        {row.disclosedTo ? (
          <div className="small">
            Disclosed to {row.disclosedTo} on {dateTime(row.disclosedAt)}: {row.disclosureSummary}
          </div>
        ) : null}

        {row.status === 'RECEIVED' || row.status === 'UNDER_REVIEW' ? (
          <div className="col gap-14">
            <div className="b">Review</div>
            <Field label="Review notes">
              <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Field>
            <TextInput label="Legal reference" value={legalReference} onChange={(e) => setLegalReference(e.target.value)} />
            <div className="row gap-6">
              <button
                className="btn"
                disabled={act.isPending || notes.trim().length < 3}
                onClick={() => act.mutate({ path: 'review', payload: { notes, legalReference } })}
              >
                Save review
              </button>
            </div>
          </div>
        ) : null}

        {row.status === 'UNDER_REVIEW' ? (
          <div className="col gap-14">
            <div className="b">Decision</div>
            {loggedByMe ? (
              <div className="small muted">You logged this request, so somebody else must decide it.</div>
            ) : (
              <>
                <Select label="Decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
                  <option value="APPROVED">Approve</option>
                  <option value="PARTIALLY_APPROVED">Approve in part</option>
                  <option value="REJECTED">Reject</option>
                </Select>
                <Field label="Reasons">
                  <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </Field>
                <div>
                  <button
                    className="btn primary"
                    disabled={act.isPending || notes.trim().length < 3}
                    onClick={() => act.mutate({ path: 'decision', payload: { decision, notes } })}
                  >
                    Record decision
                  </button>
                </div>
              </>
            )}
          </div>
        ) : null}

        {row.status === 'APPROVED' || row.status === 'PARTIALLY_APPROVED' ? (
          <div className="col gap-14">
            <div className="b">Record what was disclosed</div>
            <TextInput label="Disclosed to" value={disclosedTo} onChange={(e) => setDisclosedTo(e.target.value)} />
            <Field label="What was handed over">
              <textarea className="input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
            </Field>
            <div>
              <button
                className="btn primary"
                disabled={act.isPending || !disclosedTo.trim() || summary.trim().length < 3}
                onClick={() => act.mutate({ path: 'disclosed', payload: { disclosedTo, disclosureSummary: summary } })}
              >
                Record disclosure
              </button>
            </div>
          </div>
        ) : null}

        {row.status === 'REJECTED' || row.status === 'DISCLOSED' ? (
          <div>
            <button className="btn" disabled={act.isPending} onClick={() => act.mutate({ path: 'close' })}>
              Close
            </button>
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
